import { DISABLED_INERT } from '@/components/ui/button';
import {
  type ModelCatalog,
  type ModelTile,
  catalogFrom,
  catalogSourceSentence,
  recommendedFor,
  tileOf,
  tilesFor,
  twinOnRoute,
} from '@/lib/model-catalog';
import {
  ROUTE_LABELS,
  SEAT_KEY_GROUP,
  SEAT_KEY_LABELS,
  SEAT_KEY_NOTES,
  SEAT_KEY_ORDER,
  WARNING_WORDING,
  formatCost,
  formatEuros,
  journalValue,
  movedSettings,
  parseEuros,
  pricingPath,
  seatOf,
  seatPath,
  seatPinOf,
  settingLabel,
  tariffFor,
} from '@/lib/model-labels';
import type {
  ModelCatalogView,
  ModelNumericPath,
  ModelParamChange,
  ModelParams,
  ModelParamsView,
  ModelRoute,
  ModelSeat,
  ModelSeatKey,
  ModelTariff,
  ModelTextPath,
} from '@matter_extractor/shared';
import { estimateSeatCostEur, seatFingerprint } from '@matter_extractor/shared';
import { useId, useState } from 'react';

/**
 * Which models the dispositif runs on (§11.1) and what a step may spend on them
 * (§11.2), behind the operator guard in READING as in writing.
 *
 * Three things justify a screen rather than the `curl` the backend already
 * serves, and all three are here:
 *
 *  - a seat says what it COSTS before it is committed. The rate belongs to the
 *    seat since #61, so the card carries the two tariffs beside the model and
 *    recomputes the estimate as they are typed - shown at the only moment it
 *    can still change a mind.
 *  - the BUDGET of §11.2 sits on the same page as the rates it is compared
 *    against. A ceiling in euros read far from the euros it bounds is a number
 *    nobody can judge.
 *  - the journal of the moves made BEFORE the versions stays readable under the
 *    form (issue #192): a model changed at zero sessions is a calibration, the
 *    same change at fifteen is something else.
 *
 * Since issue #192 (vitrine.md section 5) this panel is the FORM of a versioned
 * singleton, the draft of « Les modèles »: it writes nothing and journals
 * nothing. The head of the versioned layer above it owns the autosave of the
 * draft, « Enregistrer une nouvelle version », the history and the pointers, and
 * words the refusals of a save from their `code` (`modelRefusalSentence`) -
 * the server's `message` is a developer's English string and never reaches
 * this screen. Colour stays at level 2 - black, white and bleu-nuit.
 */

export interface ModelPanelProps {
  /**
   * What sits BESIDE the form: the first-boot seed, the published tariffs, the
   * warnings and the journal of the moves made before the versions.
   */
  view: ModelParamsView;
  /** The seats of the `développement` version: what a moved seat is measured against. */
  stored: ModelParams;
  /** A version opened by « Voir », or a retired entity: nothing is typable. */
  readOnly?: boolean;
  /**
   * The providers' model lists (#175), or undefined while they are read or
   * when they could not be: the tiles then fall back on the built-in list.
   */
  catalogView?: ModelCatalogView | undefined;
  /** What the operator is editing: the draft of the version to come. */
  candidate: ModelParams;
  /** One text setting - a model id or an endpoint typed by hand. */
  onChange: (path: ModelTextPath, value: string | null) => void;
  /** One euro figure - a seat's tariff, or the budget of §11.2. */
  onNumber: (path: ModelNumericPath, value: number) => void;
  /**
   * A whole seat at once - route, model, endpoint AND tariff.
   *
   * A tile sets all four TOGETHER because it names one serve at one published
   * rate: writing them one at a time would put the candidate through states
   * nobody chose - a router route beside a direct id, which is precisely the
   * pair `route-model-mismatch` exists to name, or a new model still carrying
   * the previous one's rate, which is a euro count nothing on the screen could
   * point at.
   */
  onSeat: (seat: ModelSeatKey, next: ModelSeat) => void;
}

export function ModelPanel({
  view,
  stored,
  readOnly = false,
  catalogView,
  candidate,
  onChange,
  onNumber,
  onSeat,
}: ModelPanelProps) {
  const moved = movedSettings(stored, candidate);
  // The pins of the BASE get a tile of their own when no list names them any
  // more, so a seat still on last month's model shows it checked.
  const catalog = catalogFrom(
    catalogView,
    SEAT_KEY_ORDER.map((seat) => seatPinOf(stored, seat)),
  );

  return (
    // `inert` for a version read by « Voir »: nothing is focusable, clickable or
    // typable inside, and the layer's own banner says why.
    <div className="space-y-8" data-testid="modeles-panneau" {...(readOnly ? { inert: true } : {})}>
      {view.warnings.length > 0 ? <Warnings warnings={view.warnings} /> : null}

      {/*
        The four cards share their ROWS (BUILD-925).

        This screen exists to compare seats, and without a subgrid no line of one
        card meets its twin in the next: the seat notes are three lines on one
        and one on another, so « Fournisseur », « Modèle », the tariffs and the
        estimate all sat tens of pixels apart down the whole row - the eye could
        not scan across, on the one gesture the screen is for.

        `grid-rows-subgrid` rather than a minimum height on the note: a floor in
        pixels holds until somebody rewrites a sentence or resizes the window,
        and the next edit breaks the alignment silently. Here the row heights ARE
        the parent's, so the tallest note sets the row and all four cards move
        together whatever anybody writes.

        SEVEN rows because a seat card has seven blocks (see `SeatCard`): the
        span and the number of grid children must match, or the tail rows
        collapse into one.

        One column until `xl`, four after - the analyst of #149 beside the three
        others, as the planche `ReglagesModeles` draws them. Under 768 px - and
        all the way to 1280 - a card is the full width of the page, which is what
        keeps the two tariff fields, the tile rows and the fingerprint line from
        ever needing a horizontal scroll.
      */}
      <div data-testid="modeles-sieges" className="grid gap-6 xl:grid-cols-5">
        {SEAT_KEY_ORDER.map((seat) => (
          <SeatCard
            key={seat}
            seat={seat}
            value={candidate[seat]}
            committed={seatPinOf(view.initial, seat)}
            stored={seatPinOf(stored, seat)}
            catalog={catalog}
            tariffs={view.tariffs}
            onChange={onChange}
            onNumber={onNumber}
            onSeat={onSeat}
          />
        ))}
      </div>

      <p data-testid="modeles-catalogue-source" className="text-gray-600 text-xs">
        {catalogSourceSentence(catalogView)} La saisie à la main reste ouverte sur chaque siège.
      </p>

      <BudgetField value={candidate.budgetPerStepEur} onNumber={onNumber} />

      {moved.some((path) => seatOf(path) !== null) ? (
        <FlotteWarning stored={stored} candidate={candidate} />
      ) : null}

      {view.journal.length > 0 ? <ChangeJournal journal={view.journal} /> : null}
    </div>
  );
}

interface SeatCardProps {
  seat: ModelSeatKey;
  /** What the operator is holding, not yet written - pin AND tariff. */
  value: ModelSeat;
  /** The environment seed of the first boot - what the deploy started on. */
  committed: { route: ModelRoute; model: string; provider: string | null };
  /** What the base holds right now. */
  stored: { route: ModelRoute; model: string; provider: string | null };
  /** The tiles of this deploy (#175). */
  catalog: ModelCatalog;
  /** The published rates this deploy knows, to prefill a seat that moves. */
  tariffs: readonly ModelTariff[];
  onChange: (path: ModelTextPath, value: string | null) => void;
  onNumber: (path: ModelNumericPath, value: number) => void;
  onSeat: (seat: ModelSeatKey, next: ModelSeat) => void;
}

/**
 * One seat, chosen by clicking rather than by typing.
 *
 * The card asks the questions in the order they depend on each other - which
 * DOOR, then which model behind it, then what it costs - because a model id
 * belongs to a door: the router prefixes with a vendor and the direct API does
 * not, so the same weights are two different strings and pinning one against
 * the other is a 404 on every call of the seat.
 *
 * Free entry is one link away and stays a first-class path: the catalogue is
 * not a whitelist, and an unknown model is still accepted with a warning. It
 * also OPENS BY ITSELF when the seat runs on something no tile names - which is
 * the only way the screen can show what is actually pinned rather than showing
 * unselected tiles over a value it is hiding.
 *
 * ## Seven blocks, and a `div role="group"` rather than a `fieldset`
 *
 * The card is a SUBGRID of the panel's grid (BUILD-925), so its top level holds
 * exactly seven children - header, note, provider, model, tariffs, fingerprint,
 * tail - and spans seven rows. Add an eighth block and the span above moves with
 * it, or the last rows collapse into one.
 *
 * The container had to stop being a `<fieldset>` for that, and it was measured
 * rather than assumed: with `grid-template-rows: subgrid` on a fieldset,
 * Chromium lays the `<legend>` out beside the anonymous content box and the rows
 * are NOT shared. The accessible outcome is unchanged: a fieldset+legend maps to
 * `role="group"` named by its legend, which is literally what is spelled out
 * here, and the e2e suite finds the card by that role either way.
 */
function SeatCard({
  seat,
  value,
  committed,
  stored,
  catalog,
  tariffs,
  onChange,
  onNumber,
  onSeat,
}: SeatCardProps) {
  const [manual, setManual] = useState(false);
  const modelId = `modele-${seat}-model`;
  const providerId = `modele-${seat}-provider`;
  const titleId = `modele-${seat}-titre`;
  const pin = { route: value.route, model: value.model, provider: value.provider };
  const selected = tileOf(catalog, pin);
  // No tile names this pin: the value has to be visible somewhere, so the
  // free-entry fields are the display of last resort rather than a hidden state.
  const custom = selected === null;
  const showManual = manual || custom;
  const drifted = seatFingerprint(stored) !== seatFingerprint(committed);
  const editing = seatFingerprint(stored) !== seatFingerprint(pin);

  /**
   * A model change carries its published RATE with it, when this deploy knows
   * one - and leaves the rate alone when it does not.
   *
   * Leaving the previous model's rate behind would be the silent failure this
   * screen cannot afford: the euro count of every step would be computed at the
   * price of a model the seat stopped running, and nothing on the page would
   * point at it. Wiping it to zero instead would be just as wrong in the other
   * direction - it disarms §11.2 - so an unknown id keeps whatever is there and
   * the operator is the one who decides, which is exactly what the two fields
   * under the tiles are for.
   */
  function pinned(
    next: { route: ModelRoute; model: string; provider: string | null },
    listed: ModelTile['pricing'] = null,
  ): ModelSeat {
    // The provider's own list first (#175), then the grid the params serve.
    const published = listed ?? tariffFor(tariffs, next.model);
    if (published === null) return { ...next, pricing: value.pricing };
    return {
      ...next,
      pricing: {
        inputEurPerMillion: published.inputEurPerMillion,
        outputEurPerMillion: published.outputEurPerMillion,
      },
    };
  }

  /**
   * A door change carries the model with it, in three cases and one order.
   *
   * The TWIN when the catalogue knows the same weights on the other side - that
   * is the literal meaning of the gesture. Failing that, the target door's
   * RECOMMENDED tile, because two catalogue tiles have no twin and keeping their
   * id would put a router spelling on the direct door in one click - a 404 on
   * every call of that seat, and only a warning at the save. Failing both, the
   * id is left exactly as it is: a HAND-TYPED model is never rewritten, because
   * nobody can translate an id nobody declared, and the free-entry field is open
   * showing it.
   */
  function pickRoute(route: ModelRoute) {
    if (route === pin.route) return;
    const twin = twinOnRoute(catalog, pin, route);
    if (twin !== null) {
      onSeat(seat, pinned({ route, model: twin.id, provider: twin.provider }, twin.pricing));
      return;
    }
    const fallback = selected === null ? null : recommendedFor(catalog, seat, route);
    if (fallback !== null) {
      onSeat(
        seat,
        pinned({ route, model: fallback.id, provider: fallback.provider }, fallback.pricing),
      );
      return;
    }
    onSeat(
      seat,
      pinned({ route, model: pin.model, provider: route === 'anthropic' ? null : pin.provider }),
    );
  }

  function pickModel(tile: ModelTile) {
    onSeat(
      seat,
      pinned({ route: tile.route, model: tile.id, provider: tile.provider }, tile.pricing),
    );
  }

  return (
    // 1/7 header, 2/7 note, 3/7 provider, 4/7 model, 5/7 tariffs, 6/7
    // fingerprint, 7/7 tail.
    // biome-ignore lint/a11y/useSemanticElements: a <fieldset> cannot subgrid - measured, see above
    <div
      role="group"
      aria-labelledby={titleId}
      // One mark per seat, `modele-siege-analyst` included (#149): what the
      // capture script probes before it photographs, since a stack of `main`
      // serves three cards and no such mark.
      data-testid={`modele-siege-${seat}`}
      className={`grid gap-4 rounded-md border-2 bg-white p-5 xl:row-span-7 xl:grid-rows-subgrid ${
        editing ? 'border-bleu-nuit' : 'border-gray-200'
      }`}
    >
      <header className="space-y-1">
        <p className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
          {SEAT_KEY_GROUP[seat]}
        </p>
        <p id={titleId} className="font-semibold text-bleu-nuit text-lg">
          {SEAT_KEY_LABELS[seat]}
        </p>
      </header>

      <p className="text-gray-700 text-sm">{SEAT_KEY_NOTES[seat]}</p>

      <div className="space-y-2">
        <p className="font-semibold text-gray-800 text-sm">Fournisseur</p>
        <div
          role="radiogroup"
          aria-label={`Fournisseur - ${SEAT_KEY_LABELS[seat]}`}
          className="grid gap-2"
        >
          {ROUTE_TILE_ORDER.map((route) => (
            <Tile
              key={route}
              name={`${seat}-route`}
              checked={pin.route === route}
              onSelect={() => pickRoute(route)}
              title={ROUTE_TILE_LABELS[route]}
              detail={ROUTE_LABELS[route]}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="font-semibold text-gray-800 text-sm">Modèle</p>
        {/*
          ONE TILE PER ROW, and that is the fix rather than a wider grid
          (BUILD-925). Two columns gave an orphan half-width tile at a different
          place depending on how many tiles a door offers, so the checked one had
          no fixed place to look for. A single column is regular whatever the
          catalogue holds tomorrow, and it buys the thing this screen is for: row
          N of one card is the SAME model as row N of the next, so the seats
          compare line by line.
        */}
        <div
          role="radiogroup"
          aria-label={`Modèle - ${SEAT_KEY_LABELS[seat]}`}
          className="grid gap-2"
        >
          {tilesFor(catalog, seat, pin.route).map((tile) => (
            <Tile
              key={tile.id}
              name={`${seat}-model`}
              checked={selected?.id === tile.id}
              onSelect={() => pickModel(tile)}
              title={tile.label}
              badge={tile.recommended ? 'Recommandé' : undefined}
              detail={tile.pace}
            />
          ))}
        </div>
        {custom ? (
          <p className="text-gray-700 text-sm">
            Ce siège tourne sur un identifiant saisi à la main : <code>{value.model}</code>.
          </p>
        ) : null}
      </div>

      <SeatTariffs seat={seat} pricing={value.pricing} onNumber={onNumber} />

      <p className="text-gray-600 text-xs">
        {/*
          The model id alone rather than the whole pin: the fingerprint of a seat
          also appears in the flotte notice below, and printing the same string
          twice on one screen makes each of them ambiguous - to a reader looking
          for the one that matters, and to anything that reads this page by its
          text. Drift is still measured on the WHOLE pin.
        */}
        Au démarrage du dispositif : <code>{committed.model}</code>
        {drifted ? (
          <span className="ml-2 rounded-md border border-bleu-nuit px-2 py-0.5 text-bleu-nuit">
            déplacé depuis
          </span>
        ) : null}
      </p>

      {/*
        The tail is ONE grid child - a link, or the free-entry fields - because
        the card spans a fixed seven rows and this is the only block that changes
        shape. Being last, it is also the only one that can differ between cards
        without shifting anything above it.
      */}
      <div>
        {showManual ? null : (
          <button
            type="button"
            onClick={() => setManual(true)}
            className="text-bleu-nuit text-sm underline underline-offset-2"
          >
            Saisir un identifiant de modèle à la main
          </button>
        )}

        {showManual ? (
          <div className="space-y-3 border-gray-200 border-t pt-3">
            <div className="space-y-1">
              <label htmlFor={modelId} className="block font-semibold text-gray-800 text-sm">
                {settingLabel(seatPath(seat, 'model'))}
              </label>
              <input
                id={modelId}
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={value.model}
                onChange={(event) => onChange(seatPath(seat, 'model'), event.target.value)}
                className="h-11 w-full rounded-md border-2 border-gray-300 px-3 focus-visible:border-bleu-nuit focus-visible:outline-none"
              />
              {/*
                A hand-typed id does NOT carry a rate: nobody can look up a price
                for an id nobody declared. Said here rather than left to be
                discovered through a `pricing-missing` warning three blocks up,
                because the operator typing it is the only person who can fix it
                and this is the moment they are looking.
              */}
              <p className="text-gray-600 text-xs">
                Un identifiant saisi à la main n’apporte pas son tarif : posez-le au-dessus.
              </p>
            </div>
            <div className="space-y-1">
              <label htmlFor={providerId} className="block font-semibold text-gray-800 text-sm">
                {settingLabel(seatPath(seat, 'provider'))}
              </label>
              <input
                id={providerId}
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={value.provider ?? ''}
                disabled={pin.route === 'anthropic'}
                placeholder="aucun hébergeur épinglé"
                onChange={(event) =>
                  // An endpoint nobody pinned is `null` - a real option, and not
                  // the same thing as an empty string the schema would refuse.
                  onChange(
                    seatPath(seat, 'provider'),
                    event.target.value === '' ? null : event.target.value,
                  )
                }
                // `DISABLED_INERT` rather than a hand-rolled pair: BUILD-916
                // posed the house spelling of an off control, and a second
                // spelling drifts.
                //
                // The PLACEHOLDER is coloured explicitly, and it is the only
                // thing that needed colouring: the field is disabled exactly on
                // the direct route, and `seatPinSchema` refuses an endpoint
                // beside that route - so while it is off its value is always
                // empty and the only string on screen is the placeholder.
                // Preflight paints a placeholder at half of `currentColor`, so
                // `disabled:text-gray-600` alone would leave it at 1,93:1 on the
                // disabled ground; naming it lands at 4,55:1.
                className={`h-11 w-full rounded-md border-2 border-gray-300 px-3 focus-visible:border-bleu-nuit focus-visible:outline-none disabled:placeholder:text-gray-600 ${DISABLED_INERT}`}
              />
              {pin.route === 'anthropic' ? (
                <p className="text-gray-600 text-xs">
                  Sans objet sur l’API directe : le routeur seul choisit un hébergeur.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What a seat charges, and what that makes one session cost (§11.1).
 *
 * The estimate is recomputed HERE, from the candidate, rather than read off the
 * `estimates` the server sent: the whole point of putting the rate on the seat
 * is that somebody can change it, and a figure that only moved after a save
 * would be stale exactly while it is being decided. The arithmetic is the
 * server's own (`estimateSeatCostEur` in `shared`), so the two cannot disagree.
 *
 * The fields keep their own TEXT while they are being typed, and only push a
 * value that parses. Without that, clearing a field to retype it would write a
 * zero into the candidate for a keystroke - a rate nobody chose, which the
 * screen would then warn about with `pricing-missing`.
 */
function SeatTariffs({
  seat,
  pricing,
  onNumber,
}: {
  seat: ModelSeatKey;
  pricing: { inputEurPerMillion: number; outputEurPerMillion: number };
  onNumber: (path: ModelNumericPath, value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="font-semibold text-gray-800 text-sm">Tarif, en euros par million de jetons</p>
      {/*
        Two columns from `sm` and stacked below it: at 375 px two number fields
        side by side put their labels on three lines each, and the card is the
        full width of the page under `xl` anyway - so there is nothing to gain by
        squeezing them.
      */}
      <div className="grid gap-3 sm:grid-cols-2">
        <EuroField
          label="Entrée"
          path={pricingPath(seat, 'inputEurPerMillion')}
          value={pricing.inputEurPerMillion}
          onNumber={onNumber}
        />
        <EuroField
          label="Sortie"
          path={pricingPath(seat, 'outputEurPerMillion')}
          value={pricing.outputEurPerMillion}
          onNumber={onNumber}
        />
      </div>
      <p className="text-gray-700 text-sm">
        Estimation : <strong>{formatCost(estimateSeatCostEur(pricing, seat))}</strong>
        <span className="text-gray-600"> - indicatif, sur une session type.</span>
      </p>
    </div>
  );
}

/**
 * The AI budget of §11.2: ONE value in euros, applied to EACH STEP.
 *
 * Its own section under the seat cards rather than one more card, because it
 * belongs to no seat - it bounds what a step spends across all of them - and a
 * card would have said the opposite. The analyst's calls never count against it
 * (noyau.md §4.7): its determination is not a step. The help text says the two things H3
 * settles and that no operator can deduce from a number: it is per STEP and not
 * per session, and what happens when it is reached.
 */
function BudgetField({
  value,
  onNumber,
}: {
  value: number;
  onNumber: (path: ModelNumericPath, value: number) => void;
}) {
  return (
    <section
      aria-labelledby="budget-ia"
      data-testid="modeles-budget"
      className="space-y-3 rounded-md border-2 border-gray-200 p-5"
    >
      <h2 id="budget-ia" className="font-semibold text-bleu-nuit text-lg">
        Budget IA par étape
      </h2>
      <p className="max-w-3xl text-gray-700 text-sm">
        Une seule valeur, en euros, appliquée à <strong>chaque étape</strong> d’un protocole -
        jamais à la session entière. Quand le coût cumulé des appels d’une étape la dépasse, l’étape
        est au plafond : elle ne relance plus, et la session passe à la suite. Le coût est compté au
        tarif de chaque siège ci-dessus, donc un siège sans tarif ne consomme rien et ce plafond ne
        le retient jamais.
      </p>
      <div className="max-w-56">
        <EuroField
          label="Plafond par étape"
          path="budgetPerStepEur"
          value={value}
          onNumber={onNumber}
        />
      </div>
    </section>
  );
}

/**
 * One euro amount: a text field that keeps what was typed, and a candidate that
 * only moves on a value that parses.
 *
 * `type="text"` with `inputMode="decimal"` rather than `type="number"`, and the
 * two reasons are the same one twice: a number input reports an EMPTY string
 * for « 1,5 » in a locale whose separator is the comma, and it swallows a
 * scroll wheel over a focused field into a silent change of rate. Both failures
 * end with a euro figure nobody typed.
 */
function EuroField({
  label,
  path,
  value,
  onNumber,
}: {
  label: string;
  path: ModelNumericPath;
  value: number;
  onNumber: (path: ModelNumericPath, value: number) => void;
}) {
  const fieldId = useId();
  const [typed, setTyped] = useState<string | null>(null);
  // What the field shows: what is being typed, or the candidate. They rejoin as
  // soon as the value parses, so a candidate moved from elsewhere - a tile
  // prefilling its published rate - is visible immediately.
  const shown = typed ?? formatEuros(value);

  return (
    <div className="space-y-1">
      <label htmlFor={fieldId} className="block font-semibold text-gray-800 text-sm">
        {label}
      </label>
      <input
        id={fieldId}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        value={shown}
        onChange={(event) => {
          const text = event.target.value;
          const parsed = parseEuros(text);
          if (parsed === null) {
            setTyped(text);
            return;
          }
          setTyped(null);
          onNumber(path, parsed);
        }}
        onBlur={() => setTyped(null)}
        className="h-11 w-full rounded-md border-2 border-gray-300 px-3 focus-visible:border-bleu-nuit focus-visible:outline-none"
      />
    </div>
  );
}

/** How a door is named on its tile - short, the long sentence is underneath. */
const ROUTE_TILE_LABELS: Record<ModelRoute, string> = {
  anthropic: 'Anthropic (direct)',
  openrouter: 'OpenRouter (routeur)',
};

/**
 * The direct API first, and spelled out rather than read off the enum.
 *
 * `modelRouteSchema.options` is in the order the schema happens to declare them,
 * which puts the router ahead of the door every deploy actually starts on - a
 * reading order that says the default is the second choice.
 */
const ROUTE_TILE_ORDER: ModelRoute[] = ['anthropic', 'openrouter'];

/**
 * A tile: a `<label>` around a REAL `<input type="radio">`, kept visible.
 *
 * Both halves of that are deliberate. A native radio rather than a button with
 * `role="radio"`, so the grouping, the arrow keys and the announced state come
 * from the browser instead of from ARIA we would have to keep true by hand. And
 * VISIBLE rather than `sr-only`, because this control is what a click, a capture
 * script and a Playwright `check()` all have to reach - an element the page has
 * deliberately shrunk to a pixel is the class of flake this repository has
 * already paid for once (BUILD-794).
 *
 * « Recommandé » has no box, and that is a legibility decision rather than a
 * contrast one: `border-bleu-nuit` + `text-bleu-nuit` is exactly what « this
 * tile is selected » is drawn with here, so on an unchecked tile a boxed badge
 * read as a selection and on a checked one it dissolved into it. The word
 * survives as the small-caps grey eyebrow the card already uses for its group;
 * the single accent stays with the single thing it means.
 */
function Tile({
  name,
  checked,
  onSelect,
  title,
  detail,
  badge,
}: {
  /** The native group this radio belongs to - one per seat and per question. */
  name: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  detail: string;
  badge?: string | undefined;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-md border-2 px-3 py-2 ${
        checked ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-300 bg-white'
      }`}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        className="h-4 w-4 shrink-0 accent-bleu-nuit"
      />
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={`font-semibold text-sm ${checked ? 'text-bleu-nuit' : 'text-gray-900'}`}>
            {title}
          </span>
          {badge === undefined ? null : (
            <span className="font-semibold text-gray-700 text-xs uppercase tracking-wide">
              {badge}
            </span>
          )}
        </span>
        {/*
          `gray-700` and not `gray-600`: on the tint of a checked tile the latter
          measures 4,49:1 and misses AA for normal text by a hundredth.
        */}
        <span className="text-gray-700 text-xs">{detail}</span>
      </span>
    </label>
  );
}

/**
 * What a FLOTTE move costs, said before the write.
 *
 * The sessions created afterwards run on something else than the ones before,
 * and a session already under way does not move - it finishes on what its
 * provenance names. That is the whole sentence, and it is worth a block of its
 * own because the field that moves a model gives no hint of it.
 *
 * The pin is shown whole - `<route>:<modèle>[@<hébergeur>]` - because the model
 * id alone does not say what ran: the same model served through the router is a
 * different serve, and the router does not cache the system prompt the direct
 * API caches.
 */
function FlotteWarning({ stored, candidate }: { stored: ModelParams; candidate: ModelParams }) {
  const moved = SEAT_KEY_ORDER.filter(
    (seat) =>
      seatFingerprint(seatPinOf(stored, seat)) !== seatFingerprint(seatPinOf(candidate, seat)),
  );
  // Only the tariffs moved: a real and ordinary case since #61, and one this
  // notice has nothing to say about - a rate changes what a step COUNTS, never
  // what a model answers.
  if (moved.length === 0) return null;

  return (
    <section
      aria-labelledby="avertissement-flotte"
      className="space-y-3 rounded-md border-2 border-bleu-nuit px-5 py-4"
    >
      <h2 id="avertissement-flotte" className="font-semibold text-bleu-nuit">
        Avant d’enregistrer
      </h2>
      <p className="text-gray-800 text-sm">
        Les sessions créées après ce changement ne tournent plus sur les mêmes modèles que les
        précédentes. Rien n’est recalculé en arrière, et une session en cours ne bouge pas - elle
        finit sur ce que sa provenance nomme.
      </p>
      <dl className="space-y-3 text-sm">
        {moved.map((seat) => (
          <div key={seat} className="space-y-1">
            <dt className="font-semibold text-gray-800">{SEAT_KEY_LABELS[seat]}</dt>
            <dd className="space-y-1 text-gray-700">
              <span className="block">
                Aujourd’hui :{' '}
                <code className="text-bleu-nuit">{seatFingerprint(seatPinOf(stored, seat))}</code>
              </span>
              <span className="block">
                Ensuite :{' '}
                <code className="text-bleu-nuit">
                  {seatFingerprint(seatPinOf(candidate, seat))}
                </code>
              </span>
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-gray-700 text-sm">
        Par le routeur, le prompt système n’est pas mis en cache : chaque tour le repaie plein
        tarif, là où l’API directe le relit à un dixième du prix.
      </p>
    </section>
  );
}

/** Informative, never blocking: refusing the unknown would block the future. */
function Warnings({ warnings }: { warnings: ModelParamsView['warnings'] }) {
  return (
    <div className="space-y-2 rounded-md border border-gray-300 px-5 py-4">
      <p className="font-semibold text-gray-900">À savoir :</p>
      <ul className="space-y-1 text-gray-800 text-sm">
        {warnings.map((warning) => (
          <li key={`${warning.path}-${warning.code}`}>
            <strong>{settingLabel(warning.path)}</strong> - {WARNING_WORDING[warning.code]} (
            {warning.model}).
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The journal of the moves made BEFORE the versions (issue #192): nothing writes
 * it any more - a version and its history replace it - but what it holds stays
 * readable, and it is absent altogether on a deploy that never filled it.
 */
function ChangeJournal({ journal }: { journal: readonly ModelParamChange[] }) {
  return (
    <section aria-labelledby="journal-modeles" className="space-y-3">
      <h2 id="journal-modeles" className="font-semibold text-bleu-nuit text-lg">
        Ce qui a bougé avant les versions
      </h2>
      {journal.length === 0 ? null : (
        <ul className="space-y-2">
          {journal.map((change) => (
            <li
              key={`${change.changedAt}-${change.path}`}
              className="rounded-md border border-gray-200 bg-white p-3 text-sm"
            >
              <span className="text-gray-600">{change.changedAt.slice(0, 10)}</span>{' '}
              <span className="font-semibold text-bleu-nuit">{change.changedBy}</span> a remplacé{' '}
              {settingLabel(change.path)} : {journalValue(change.path, change.from)} devient{' '}
              {journalValue(change.path, change.to)}, à {change.sessionCountAtChange} session
              {change.sessionCountAtChange > 1 ? 's' : ''} déjà menée
              {change.sessionCountAtChange > 1 ? 's' : ''}.
              {change.note === null ? null : (
                <span className="text-gray-700"> Motif : {change.note}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
